import { Document } from '@roc/feature-types';
import { useDocumentStore } from '@roc/feature-documents';
import { Grid, Typography, Divider } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { createStyles } from '@material-ui/core/styles';
import { BorrowerBaseballCardBaseStore } from 'libs/feature-borrowers/src/borrowers/stores/borrowerBaseballCardBaseStore';
import { observer } from 'mobx-react';
import { ADD_PROPERTY, EDIT_PROPERTY } from './utils';
import { isNil } from '@roc/feature-utils';
import { DialogState, StandardDialog, Button, FileUpload, FileUploadArea, FileUploadAreaWithDownload, createAutocompleteAddressField, createCurrencyField, createDateField, createRadioField, createSelectField, createTextField, createCheckboxField } from '@roc/ui';
import { stateOptions } from 'libs/client-portal-shared/src/app/modules/bridgePricer/utils/bridgePricerConstants';
import { dealTypeOptions } from 'libs/feature-borrowers/src/borrowerDetailsVerification/components/trackRecordVerification/subcomponents/trackRecordFirstStep/trackRecordPropertyForm';
import { BorrowerTrackRecordStore } from '@roc/feature-borrowers';
import { useEffect } from 'react';
import { useUserStore } from '@roc/feature-app-core';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    typography: {
      marginTop: 0,
    },
    dialogContent: {
      padding: 0,
    },
    button: {
      marginRight: theme.spacing(1)
    }
  })
);

export const AddNewPropertyForm = observer(({ store, trackRecordStore, currentBorrower }) => {
  const classes = useStyles();

  const { borrowerTrackRecordVerificationStore } = trackRecordStore;
  const propertyFormStore = borrowerTrackRecordVerificationStore.trackRecordPropertyFormStore;
  const { propertyInformationDialogState } = store;
  const baseBallCardFormStore = store.propertyFormStore;

  const handleAddressChange = event => {
    const { value, fullInfo } = event?.target || {};
    const { address_components, geometry } = fullInfo || {};
    propertyFormStore.saveAddressField(value, address_components, geometry);
  };

  const getItemListFromBothSets = () => {
    const firstArray = borrowerTrackRecordVerificationStore.currentDocumentsToUpload;
    const secondArray = borrowerTrackRecordVerificationStore.currentTasks;

    const mergedArray = firstArray.map((itemA) => {
      const itemB = secondArray.find((itemB) => itemB.originalFileName === itemA.name);
      return itemB ? { ...itemA, taskId: itemB.taskId, originalFileName: itemB.originalFileName, fileExtension: itemB.fileExtension }
        : itemA;
    })
    return mergedArray;
  }

  const handleCloseModal = async () => {
    borrowerTrackRecordVerificationStore.setPropertyFormActive(false);
    store.setPropagateAddress(false);
    store.closePropertyInformationDialog();
  };

  const handleSavePropertyInformation = async () => {
    if (borrowerTrackRecordVerificationStore.trackRecordPropertyFormStore.form.meta.isValid) {
      borrowerTrackRecordVerificationStore.setShouldPropagateDealType(false);
      await borrowerTrackRecordVerificationStore.saveTrackRecordProperty();
      await store.handleSaveProperty();
      store.setPropagateAddress(false);
      store.closePropertyInformationDialog();
    }
    else {
      borrowerTrackRecordVerificationStore.trackRecordPropertyFormStore.runFormValidation();
      borrowerTrackRecordVerificationStore.globalStore.notificationStore.showErrorNotification({
        message: 'Please fill the missing fields',
      });
    }
  }

  useEffect(() => {
    borrowerTrackRecordVerificationStore.borrowerId = currentBorrower;
  }, [])

  useEffect(() => {
    if (store.propagateAddress) {
      if (baseBallCardFormStore?.form?.fields?.purchaseAddress?.value !== '') {
        propertyFormStore.onFieldChange('fullAddress', baseBallCardFormStore?.form?.fields?.purchaseAddress?.value);
        propertyFormStore.onFieldChange('purchaseAddress', baseBallCardFormStore?.form?.fields?.purchaseAddress?.value);
        propertyFormStore.onFieldChange('purchaseState', baseBallCardFormStore?.form?.fields?.purchaseState?.value);
        propertyFormStore.onFieldChange('purchaseZipcode', baseBallCardFormStore?.form?.fields?.purchaseZipcode?.value);
        propertyFormStore.onFieldChange('purchaseCity', baseBallCardFormStore?.form?.fields?.purchaseCity?.value);
      }
    }
  }, [store])

  return (
    <>
      <Grid item xs={12} container direction='row' spacing={1}>
        {store.hasNoTrackRecordResults  && <Grid item xs={12} style={{ display: 'flex', justifyContent: 'flex-start', paddingBottom: '22px', paddingTop: '22px' }}>
          <Typography variant="body2">
            We are not able to locate <strong>public track records</strong> for your property. Please enter de details below.
          </Typography>
        </Grid>}
        <Grid item xs={6}>
          {createAutocompleteAddressField({
            fieldName: 'fullAddress',
            label: 'Address Line 1',
            testId: 'fullAddress',
            store: propertyFormStore,
            onChange: handleAddressChange,
          })}
        </Grid>
        <Grid item xs={6}>
          {createTextField({
            label: 'Address Line 2',
            fieldName: 'address2',
            testId: 'address2',
            store: propertyFormStore,
          })}
        </Grid>
        <Grid item xs={4}>
          {createTextField({
            label: 'City',
            fieldName: 'purchaseCity',
            testId: 'purchaseCity',
            store: propertyFormStore,
          })}
        </Grid>
        <Grid item xs={4}>
          {createSelectField({
            store: propertyFormStore,
            testId: 'purchaseState',
            fieldName: 'purchaseState',
            label: 'State',
            options: stateOptions,
          })}
        </Grid>
        <Grid item xs={4}>
          {createTextField({
            label: 'Zip Code',
            fieldName: 'purchaseZipcode',
            testId: 'purchaseZipcode',
            store: propertyFormStore,
          })}
        </Grid>
        <Grid item xs={6}>
          {createTextField({
            store: propertyFormStore,
            testId: 'entityName',
            fieldName: 'entityName',
            label: 'Entity on Title',
          })}
        </Grid>
        {((propertyFormStore.groundUpSelected || propertyFormStore.bridgeSelected) || (!propertyFormStore.termSelected && !propertyFormStore.groundUpSelected && !propertyFormStore.bridgeSelected)) && (
          <>
            <Grid item xs={6}>
              {createCurrencyField({
                label: 'Renovation Budget',
                fieldName: 'rehabBudget',
                testId: 'rehabBudget',
                store: propertyFormStore,
              })}
            </Grid>
          </>)}
        <Grid item xs={6}>
          {createCurrencyField({
            store: propertyFormStore,
            label: 'Purchase Amount',
            fieldName: 'purchasePrice',
            testId: 'purchasePrice',
          })}
        </Grid>
        {propertyFormStore.form.fields.dealType.value != 'Rental' ?
          (<Grid item xs={6}>
            {createDateField({
              label: 'Purchase Date',
              fieldName: 'purchaseDate',
              testId: 'purchaseDate',
              format: 'MM/dd/yyyy',
              store: propertyFormStore,
            })}
          </Grid>) :
          (<Grid item xs={6}>
            {createDateField({
              label: 'Purchase Date',
              fieldName: 'purchaseDate',
              testId: 'purchaseDate',
              format: 'MM/dd/yyyy',
              store: propertyFormStore,
            })}
          </Grid>)}
        <Grid item xs={6}>
          {createTextField({
            store: propertyFormStore,
            label: 'Seller Name',
            fieldName: 'purchasedFrom',
            testId: 'purchasedFrom',
          })}
        </Grid>
        <Grid item xs={6}>
          {createDateField({
            store: propertyFormStore,
            label: 'Sale Date',
            fieldName: 'saleDate',
            testId: 'saleDate',
          })}
        </Grid>
        <Grid item xs={6}>
          {createCurrencyField({
            store: propertyFormStore,
            label: 'Sale Amount',
            fieldName: 'salePrice',
            testId: 'salePrice',
          })}
        </Grid>
        {propertyFormStore.form.fields.dealType.value != 'Rental' && (
          <>
          </>
        )}

        {<Grid item xs={12} style={{ marginTop: '10px', marginBottom: '20px' }}>
          <Typography variant="body1" gutterBottom>
            Deal Type
          </Typography>
          <Grid container spacing={2} direction="row" alignItems="flex-start">
            <Grid item>
              {createCheckboxField({
                label: 'Fix And Flip',
                fieldName: 'bridge',
                store: propertyFormStore,
                disabled: propertyFormStore.groundUpSelected
              })}
            </Grid>
            <Grid item>
              {createCheckboxField({
                label: 'Rental',
                fieldName: 'term',
                store: propertyFormStore,
                disabled: (propertyFormStore.groundUpSelected || propertyFormStore.bridgeSelected)
              })}
            </Grid>
            <Grid item>
              {createCheckboxField({
                label: 'Ground Up',
                fieldName: 'groundUp',
                store: propertyFormStore,
                disabled: (propertyFormStore.bridgeSelected && !propertyFormStore.groundUpSelected)
              })}
            </Grid>
          </Grid>
        </Grid>}
      </Grid>
      <Grid item xs={12}>
        <span>{`Supporting Documents`}</span>
      </Grid>
      <Grid item xs={12}>
        <FileUploadAreaWithDownload
          items={
            getItemListFromBothSets()
          }
          setItems={(updateFunc) => {
            const updatedItems = updateFunc(borrowerTrackRecordVerificationStore.currentDocumentsToUpload);
            borrowerTrackRecordVerificationStore.currentDocumentsToUpload = updatedItems;
          }}
          multiple={true}
          store={borrowerTrackRecordVerificationStore}
        />
      </Grid>
      {propertyInformationDialogState != DialogState.ADD && <Grid item xs={12} style={{ paddingBottom: '10px' }}>
        {createTextField({
          label: 'Comments',
          fieldName: 'comment',
          testId: 'comment',
          store: propertyFormStore,
          multiline: true,
          minRows: 2
        })}
      </Grid>}
      <Grid container justifyContent='flex-end'>
        <Button
          variant='outlined'
          color='primary'
          onClick={() => { handleCloseModal() }}
          testId='removeProperty'
          className={classes.button}
          style={{ backgroundColor: 'transparent' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => { handleSavePropertyInformation() }}
          testId='saveProperty'
          className={classes.button}
        >
          Save
        </Button>
      </Grid>
    </>
  );
});